import { Component } from '@angular/core';
import { map } from 'rxjs/operators';
import { EstablishmentService } from '../../establishment/establishment.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SupplierService } from '../../supplier/supplier.service';
import { ContractStatusEnum, contractStatusTranslationObject } from '../../../../../core/enums/contract-status.enum';
import { AuthService } from '../../../../../core/services/auth.service';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { ContractService } from '../contract.service';
import { SidenavBaseFilterComponent } from '../../../../../core/base/components/sidenav/base-filter/sidenav-base-filter.component';
import { CONTRACT_FILTER_SCHEMA, toActivityOption } from './contract-filter.schema';
import { ActivityTypeService } from '../../../settings/activity-type/activity-type.service';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { AutocompleteService } from '../../../../../core/services/scroll.service';

@Component({
  selector: 'contract-filter',
  templateUrl: './contract-filter.component.html',
  styleUrls: ['./contract-filter.component.scss'],
})
export class ContractFilterComponent extends SidenavBaseFilterComponent {
  override readonly schema = CONTRACT_FILTER_SCHEMA;
  contractStatusOptions = [];
  showEstablishments: boolean;

  constructor(
    public establishmentService: EstablishmentService,
    public suppliersService: SupplierService,
    public authService: AuthService,
    public userProfileService: UserProfileService,
    public contractService: ContractService,
    public activityService: ActivityTypeService,
    public autocompleteService: AutocompleteService,
    public snackbarService: SnackbarService,
  ) {
    super(snackbarService, autocompleteService);

    this.addForm(
      'standardForm',
      new UntypedFormGroup({
        technicalIdentifier: new UntypedFormControl(),
        establishments: new UntypedFormControl(),
        suppliers: new UntypedFormControl(),
        supplierContractRef: new UntypedFormControl(),
        status: new UntypedFormControl(),
        businessReferentUsers: new UntypedFormControl(),
        activities: new UntypedFormControl(),
      }),
    );

    this.addForm(
      'applicationPeriodForm',
      new UntypedFormGroup({
        comparison: new UntypedFormControl(),
        min: new UntypedFormControl(),
        max: new UntypedFormControl(),
      }),
    );

    this.addForm(
      'endApplicationPeriodForm',
      new UntypedFormGroup({
        comparison: new UntypedFormControl(),
        min: new UntypedFormControl(),
        max: new UntypedFormControl(),
      }),
    );

    this.addForm(
      'noticePeriodForm',
      new UntypedFormGroup({
        comparison: new UntypedFormControl(),
        min: new UntypedFormControl(),
        max: new UntypedFormControl(),
      }),
    );

    this.addForm(
      'revisionPeriodForm',
      new UntypedFormGroup({
        comparison: new UntypedFormControl(),
        min: new UntypedFormControl(),
        max: new UntypedFormControl(),
      }),
    );

    const allStatuses = Object.values(ContractStatusEnum).map(status => contractStatusTranslationObject(status));

    this.contractStatusOptions = this.authService.isReferentEngagement()
      ? allStatuses
      : allStatuses.filter(status => status.value !== ContractStatusEnum.DRAFT);

    this.showEstablishments = (this.authService.currentUserValue().affectations?.length ?? 0) > 1;
  }

  initialize(): void {
    this.autocompleteEstablishment('');
    this.autocompleteSupplier('');
    this.autocompleteBusinessReferentUsers('');
    this.autocompleteContractSupplierContractRef('');
    this.autocompleteActivity('');
  }

  autocompleteEstablishment(search: string = '', pageToGo?: 'next' | 'previous') {
    this.autocompleteService
      .autocompleteInfiniteScroll(search, 'establishments', '_id', pageToGo, (page, size) =>
        this.establishmentService.findAll(page, size, 'label', 'asc', search, { onlyMine: true }),
      )
      ?.subscribe();
  }

  autocompleteSupplier(search: string = '', pageToGo?: 'next' | 'previous') {
    this.autocompleteService
      .autocompleteInfiniteScroll(search, 'suppliers', '_id', pageToGo, (page, size) =>
        this.suppliersService.findAll(page, size, 'label', 'asc', search),
      )
      ?.subscribe();
  }

  autocompleteBusinessReferentUsers(search: string = '', pageToGo?: 'next' | 'previous') {
    this.autocompleteService
      .autocompleteInfiniteScroll(search, 'businessReferentUsers', '_id', pageToGo, (page, size) =>
        this.userProfileService.findAll(page, size, 'lastName', 'asc', search, { isReferentEngagement: true }),
      )
      ?.subscribe();
  }

  autocompleteContractSupplierContractRef(search: string = '', pageToGo?: 'next' | 'previous') {
    this.autocompleteService
      .autocompleteInfiniteScroll(search, 'supplierContractRef', '_id', pageToGo, (page, size) =>
        this.contractService.findAll(page, size, 'supplierContractReference', 'asc', search, {
          supplierContractRefOnly: true,
        }),
      )
      ?.subscribe();
  }

  autocompleteActivity(search: string = '', pageToGo?: 'next' | 'previous') {
    this.autocompleteService
      .autocompleteInfiniteScroll(search, 'activities', '_id', pageToGo, (page, size) =>
        this.activityService.findAll(page, size, 'label', 'asc', search).pipe(
          map(response => ({
            ...response,
            data: response.data.map(toActivityOption),
          })),
        ),
      )
      ?.subscribe();
  }

  onScrollLine(pageToGo: 'next' | 'previous', filterField: string): void {
    switch (filterField) {
      case 'establishments':
        this.autocompleteEstablishment('', pageToGo);
        break;
      case 'suppliers':
        this.autocompleteSupplier('', pageToGo);
        break;
      case 'businessReferentUsers':
        this.autocompleteBusinessReferentUsers('', pageToGo);
        break;
      case 'supplierContractRef':
        this.autocompleteContractSupplierContractRef('', pageToGo);
        break;
      case 'activities':
        this.autocompleteActivity('', pageToGo);
        break;
      default:
        console.error('Unknown filterField:', filterField);
    }
  }
}
